<template>
  <vx-card id="print-report" class="w-full feedback-actions">
    <!-- 주요의견 및 조치사항 -->
    <h5><i class="dot"></i>주요의견 및 조치사항</h5>

    <vs-tabs v-model="tabIndex">
      <vs-tab v-for="tab of TABS" :key="`tab-${tab.boardId}`" :label="tab.label" />
    </vs-tabs>

    <div class="flex flex-wrap items-center mb-3 gap-2">
      <div class="flex items-center justify-center gap-3">
        <vs-select v-model="selectedYear" autocomplete @change="loadPost()">
          <vs-select-item v-for="year in years" :key="`year-${year}`" :value="year" :text="year" />
        </vs-select>
        <vs-select v-model="selectedMonth" autocomplete @change="loadPost()" :style="{ width: tabBoardId === 821 ? '120px' : '80px' }">
          <vs-select-item v-for="p in periods" :key="`period-${p.value}`" :value="p.value" :text="p.text" />
        </vs-select>
      </div>
      <div v-if="0 < Object.keys(list).length" class="w-full sm:w-auto ml-0 sm:ml-auto flex flex-wrap gap-2">
        <!-- v-show: 누른 버튼이 바로 사라지면 vuesax 리플 처리에서 clientWidth 오류가 난다 -->
        <vs-button v-show="!isEditMode" class="w-full flex-1" color="secondary" type="border" @click="onClickEnterResults">조치 결과 입력</vs-button>
        <vs-button v-show="isEditMode" color="secondary" type="border" @click="onClickEnterResultsCancel">취소</vs-button>
        <vs-button v-show="isEditMode" color="secondary" @click="onClickEnterResultsSave">저장</vs-button>
      </div>
    </div>

    <div class="w-full overflow-x-auto">
      <table>
        <colgroup>
          <col width="130" />
          <col width="120" />
          <col width="320" />
          <col width="260" />
          <col width="320" />
          <col width="150" />
        </colgroup>
        <thead>
          <tr>
            <th rowspan="2">구분</th>
            <th colspan="5">{{ currentTab.title }}</th>
          </tr>
          <tr>
            <th>발언자</th>
            <th>주요 의견</th>
            <th>조치 계획</th>
            <th>조치 결과</th>
            <th>개선결과</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in tableRows" :key="row.key" :class="rowClass(row)" @click="onClickRow(row)">
            <td v-if="row.siteFirst" :rowspan="row.siteSpan" class="bg-th">{{ row.label }}</td>

            <!-- 특이사항 없음 / 의견 없음 -->
            <td v-if="row.type === 'note'" colspan="5" class="text-left muted">{{ row.text }}</td>

            <template v-else>
              <td v-if="row.speakerFirst" :rowspan="row.speakerSpan" class="text-left whitespace-pre-wrap">{{ row.opinion.발언자 }}</td>
              <!-- 옛 문서(발언자 없는 의견 1건)는 글 자체에 번호가 있어 번호를 붙이지 않는다 -->
              <td class="whitespace-pre-wrap text-left"><template v-if="row.post.의견목록.length > 1 || row.opinion.발언자">{{ row.index + 1 }}. </template>{{ row.opinion.의견 }}</td>
              <td class="whitespace-pre-wrap text-left">{{ row.opinion.조치계획 }}</td>

              <!-- 조치 결과 -->
              <td v-if="!isEditMode" class="whitespace-pre-wrap text-left">
                <span v-if="row.group.letter" class="tag">묶음 {{ row.group.letter }}<template v-if="row.group.head"> · 의견 {{ row.group.numbers }}</template></span>
                <template v-if="row.group.head">
                  <span v-if="row.opinion.조치결과">{{ row.opinion.조치결과 }}</span>
                  <span v-else class="muted">미입력</span>
                </template>
                <span v-else class="muted">묶음 {{ row.group.letter }} 결과와 같음</span>
              </td>
              <td v-else class="text-left editMode">
                <template v-if="row.group.head">
                  <span v-if="row.group.letter" class="tag">묶음 {{ row.group.letter }} · 의견 {{ row.group.numbers }}</span>
                  <!-- 묶을 의견을 고르는 중(picking)에는 결과 입력을 잠근다. 묶으면 머리 의견 값으로 덮이므로 그 사이의 수정이 사라지지 않게 -->
                  <MaxLengthTextarea rows="5" v-model="row.opinion.조치결과" class="w-full mb-0" :readonly="picking" />
                  <div class="flex flex-wrap items-center gap-3 mt-1">
                    <label class="pick" :class="{ disabled: isPickDisabled(row) }">
                      <input type="checkbox" :checked="isPicked(row)" :disabled="isPickDisabled(row)" @change="togglePick(row)" /> 묶을 의견 선택
                    </label>
                    <!-- 체크된 행마다 묶기 버튼: 누른 의견의 조치결과·개선결과가 묶음의 기준이 된다 -->
                    <button v-if="isPicked(row) && picked.length >= 2" type="button" class="join-btn" @click="joinPicked(row)">이 의견 결과로 묶기</button>
                    <span v-else-if="isPicked(row)" class="muted text-sm">묶을 의견을 선택하세요</span>
                    <button v-if="row.group.letter" type="button" class="link" :disabled="picking" @click="splitGroup(row)">묶음 풀기</button>
                  </div>
                </template>
                <template v-else>
                  <span class="tag">묶음 {{ row.group.letter }}</span>
                  <span class="muted">묶음 {{ row.group.letter }} 결과와 같음</span>
                  <div class="mt-1"><button type="button" class="link" :disabled="picking" @click="leaveGroup(row)">묶음에서 빼기</button></div>
                </template>
              </td>

              <!-- 개선결과 -->
              <td v-if="!isEditMode">
                <span class="status" :class="statusClass(row.group.headOpinion.개선결과)">{{ row.group.headOpinion.개선결과 || "미입력" }}</span>
                <div v-if="row.group.headOpinion.개선결과 === '기타' && row.group.headOpinion.기타내용" class="muted text-sm mt-1">{{ row.group.headOpinion.기타내용 }}</div>
              </td>
              <td v-else class="editMode text-left">
                <template v-if="row.group.head">
                  <label v-for="st in 개선결과목록" :key="`${row.key}-${st}`" class="radio-line">
                    <input type="radio" :name="`st-${row.key}`" :value="st" v-model="row.opinion.개선결과" :disabled="picking" /> {{ st }}
                  </label>
                  <ml-input v-if="row.opinion.개선결과 === '기타'" v-model="row.opinion.기타내용" placeholder="기타 내용" class="w-full mt-1" :readonly="picking" />
                </template>
                <span v-else class="muted">묶음 {{ row.group.letter }}와 같음</span>
              </td>
            </template>
          </tr>
          <tr v-if="0 === tableRows.length">
            <td colspan="6">표시할 데이터가 없습니다.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </vx-card>
</template>

<script>
import MaxLengthTextarea from "@/components/MaxLengthTextarea.vue";
import DateUtil from "@/util/DateUtil.js";
import { normalizeOpinions, applyOpinions, getQuarter, 개선결과목록, nextGroupId, sortSites, isSharedGroupId } from "./opinion.js";

const TABS = [
  { boardId: 818, label: "산업안전보건위원회", title: "산업안전보건위원회(1회/분기)" },
  { boardId: 819, label: "안전보건협의체", title: "안전보건협의체(1회/월)" },
  { boardId: 820, label: "설비안전위원회", title: "설비안전위원회(1회/월)" },
  { boardId: 821, label: "크레인안전협의체", title: "크레인안전협의체(1회/2개월)" },
];
const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// 오늘이 속한 기간 선택값: 818 분기(1~4), 821 2개월 시작 월(1,3,5,…), 그 외 월
function todayPeriod(tabLabel) {
  const month = new Date().getMonth() + 1;
  if (tabLabel === "산업안전보건위원회") return Math.ceil(month / 3);
  if (tabLabel === "크레인안전협의체") return month % 2 ? month : month - 1;
  return month;
}

export default {
  components: { MaxLengthTextarea },
  data() {
    return {
      TABS,
      개선결과목록,
      isEditMode: false,
      tabIndex: 0,
      selectedYear: new Date().getFullYear(),
      years: [],
      selectedMonth: todayPeriod(TABS[0].label), // 818: 분기(1~4), 819·820: 월, 821: 시작 월(1,3,5,…). 처음엔 오늘 기준

      // list[fieldId] = { label, posts: [{ postId, originData, 특이사항없음, legacy, 의견목록 }] }
      list: {},
      editList: null, // 조치 결과 입력 중인 복사본
      picked: [], // 묶기 위해 고른 행 key
      highlightFieldId: null, // 실시현황에서 넘어온 사업장
      skipTabConvert: false,
    };
  },
  computed: {
    projectId() {
      return parseInt(this.$route.params.proj_id);
    },
    currentTab() {
      return TABS[this.tabIndex] || TABS[0];
    },
    tabBoardId() {
      return this.currentTab.boardId;
    },
    periods() {
      if (this.tabBoardId === 818) return [1, 2, 3, 4].map((q) => ({ value: q, text: `${q}분기` }));
      if (this.tabBoardId === 821) return [1, 3, 5, 7, 9, 11].map((m) => ({ value: m, text: `${m}월~${m + 1}월` }));
      return Array.from({ length: 12 }, (_, i) => ({ value: i + 1, text: `${i + 1}월` }));
    },
    source() {
      return (this.isEditMode && this.editList) || this.list;
    },
    // 묶을 의견을 고르는 중. 이 동안은 조치결과·개선결과 입력과 묶음 풀기를 잠근다.
    picking() {
      return this.isEditMode && this.picked.length > 0;
    },
    // 사업장 → 문서 → 의견 순으로 편 표 행
    tableRows() {
      const rows = [];
      // 사업장 행은 실시현황과 같은 순서(본사 → 인천 → 포항 → 당진 → 중앙기술연구소, 없는 사업장은 건너뜀)
      const fieldIds = sortSites(Object.keys(this.source), (fieldId) => this.source[fieldId].label);
      for (const fieldId of fieldIds) {
        const site = this.source[fieldId];
        const siteRows = [];
        for (const post of site.posts) {
          if (post.특이사항없음) {
            siteRows.push({ type: "note", key: `${post.postId}-none`, post, text: "특이사항 없음" });
            continue;
          }
          if (!post.의견목록.length) {
            siteRows.push({ type: "note", key: `${post.postId}-empty`, post, text: "작성된 종사자 의견이 없습니다." });
            continue;
          }
          post.의견목록.forEach((opinion, index) => {
            const prev = post.의견목록[index - 1];
            const speakerFirst = index === 0 || prev.발언자 !== opinion.발언자;
            let speakerSpan = 1;
            if (speakerFirst) while (post.의견목록[index + speakerSpan] && post.의견목록[index + speakerSpan].발언자 === opinion.발언자) speakerSpan++;
            siteRows.push({ type: "opinion", key: `${post.postId}-${index}`, post, opinion, index, speakerFirst, speakerSpan, group: null });
          });
        }
        // 묶음은 사업장 단위로 계산한다. 같은 사업장이면 문서(회의)가 달라도 "g:" 공유 ID 로 묶일 수 있다.
        const opinionRows = siteRows.filter((r) => r.type === "opinion");
        this.groupInfo(opinionRows).forEach((g, i) => (opinionRows[i].group = g));
        siteRows.forEach((row, i) => {
          row.fieldId = fieldId;
          row.label = site.label;
          row.siteFirst = i === 0;
          row.siteSpan = siteRows.length;
        });
        rows.push(...siteRows);
      }
      return rows;
    },
    permission() {
      const boards = this.$store.state.member.memberInfo.boards;
      if (boards) {
        let board = boards.find((b) => parseInt(b.id) === parseInt(this.tabBoardId));
        if (!!board && !!board.pivot) {
          if (board.pivot.project_id) {
            // 프로젝트에 맞는 권한이 있는지 다시 조사한다
            board = boards.find((b) => b.pivot && parseInt(b.id) === parseInt(this.tabBoardId) && parseInt(b.pivot.project_id) === parseInt(this.projectId));
            if (!!board && !!board.pivot) return { ...board.pivot };
          } else {
            return { ...board.pivot };
          }
        }
      }
      return { c: 0, r: 0, u: 0, d: 0, v: 0 };
    },
  },
  watch: {
    tabIndex(newIndex, oldIndex) {
      if (this.skipTabConvert) return;
      this.selectedMonth = this.updateSelectedMonth(TABS[newIndex].label, TABS[oldIndex].label);
      this.loadPost();
    },
  },
  created() {
    this.generateYears();
    this.loadPost();
  },
  methods: {
    generateYears() {
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 50;
      for (let year = currentYear; year >= startYear; year--) {
        this.years.push(year);
      }
    },

    // 실시현황(Status) 셀 클릭 → 해당 회의·기간·사업장으로 이동. period 는 selectedMonth 와 같은 의미.
    async showMeeting({ boardId, year, period, fieldId }) {
      const index = TABS.findIndex((t) => t.boardId === boardId);
      if (index < 0) return;
      this.skipTabConvert = true;
      this.tabIndex = index;
      this.selectedYear = year;
      this.selectedMonth = period;
      await this.$nextTick();
      this.skipTabConvert = false;
      await this.loadPost(fieldId);
      this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    // 묶음 키. 숫자 묶음은 문서 안에서만 같으면 되고, "g:" 공유 ID 는 사업장 안의 다른 문서와도 같은 묶음이다.
    groupKey(post, opinion) {
      return isSharedGroupId(opinion.묶음) ? opinion.묶음 : `${post.postId}:${opinion.묶음}`;
    },
    // 문서가 여럿일 때 의견 번호 앞에 붙이는 문서 표시 (작성 월)
    postTag(post) {
      if (!post.header) post.header = JSON.parse(post.originData.post_content).header || {};
      return post.header.month ? `${Number(post.header.month)}월 ` : "";
    },
    // 사업장 안 의견 행(문서 순)들의 묶음 정보. 2건 이상인 묶음에만 A, B… 글자가 붙고, 머리 의견(처음 나오는 의견)이 결과를 가진다.
    groupInfo(rows) {
      const byGroup = {};
      rows.forEach((r, i) => {
        const k = this.groupKey(r.post, r.opinion);
        (byGroup[k] = byGroup[k] || []).push(i);
      });
      const letters = {};
      let n = 0;
      Object.keys(byGroup)
        .sort((a, b) => byGroup[a][0] - byGroup[b][0])
        .forEach((k) => {
          if (byGroup[k].length > 1) letters[k] = LETTERS[n++] || `${n}`;
        });
      return rows.map((r, i) => {
        const k = this.groupKey(r.post, r.opinion);
        const idx = byGroup[k];
        const members = idx.map((x) => rows[x]);
        const headPost = rows[idx[0]].post;
        // 다른 문서의 의견은 "2월 3" 처럼 작성 월을 붙여 구분한다
        const numbers = members.map((m) => `${m.post.postId !== headPost.postId ? this.postTag(m.post) : ""}${m.index + 1}`).join(", ");
        return { letter: letters[k] || "", head: idx[0] === i, headOpinion: rows[idx[0]].opinion, members, numbers };
      });
    },
    // 사업장 안 모든 문서의 묶음 구성원에 머리 의견의 결과를 복사한다 (저장 직전)
    syncGroups(posts) {
      const heads = {};
      for (const post of posts) {
        for (const o of post.의견목록) {
          const k = this.groupKey(post, o);
          if (heads[k] === undefined) heads[k] = o;
          else this.copyResult(heads[k], o);
        }
      }
    },

    rowClass(row) {
      return {
        editMode: this.isEditMode,
        highlight: this.highlightFieldId !== null && `${row.fieldId}` === `${this.highlightFieldId}`,
        dim: this.isPickDisabled(row), // 묶기 선택 중, 다른 사업장(다른 문서) 행
      };
    },
    statusClass(status) {
      return { done: status === "완료", doing: status === "진행중", etc: status === "기타", muted: !status };
    },

    isPicked(row) {
      return this.picked.includes(row.key);
    },
    // 처음 고른 의견의 사업장. 묶기는 같은 사업장 안에서만 되므로(문서가 달라도 된다) 그 밖의 행은 고를 수 없게 한다.
    pickedFieldId() {
      if (!this.picked.length) return null;
      const first = this.tableRows.find((r) => r.type === "opinion" && this.picked.includes(r.key));
      return first ? `${first.fieldId}` : null;
    },
    isPickDisabled(row) {
      if (!this.isEditMode || !row.post) return false;
      const fieldId = this.pickedFieldId();
      return fieldId !== null && `${row.fieldId}` !== fieldId;
    },
    togglePick(row) {
      if (this.isPicked(row)) this.picked = this.picked.filter((k) => k !== row.key);
      else if (!this.isPickDisabled(row)) this.picked.push(row.key);
    },
    // 고른 의견(과 그 묶음 구성원)들을 headRow 의견의 묶음으로 합치고, 그 의견의 조치결과·개선결과를 모두에 맞춘다.
    // 같은 사업장이면 문서가 달라도 묶인다. 문서를 넘는 묶음은 "g:" 로 시작하는 공유 ID 를 쓰고, 문서마다 따로 저장된다.
    joinPicked(headRow) {
      const picks = this.tableRows.filter((r) => r.type === "opinion" && this.picked.includes(r.key));
      if (picks.length < 2) return this.customAlert("묶을 의견을 두 개 이상 선택해 주세요.", "의견 묶기");
      if (new Set(picks.map((r) => `${r.fieldId}`)).size > 1) return this.customAlert("같은 사업장의 의견끼리만 묶을 수 있습니다.", "의견 묶기");

      const head = headRow.opinion;
      const keys = new Set(picks.map((r) => this.groupKey(r.post, r.opinion)));
      const siteRows = this.tableRows.filter((r) => r.type === "opinion" && `${r.fieldId}` === `${headRow.fieldId}`);
      const targets = siteRows.filter((r) => keys.has(this.groupKey(r.post, r.opinion)));
      const multiPost = new Set(targets.map((r) => r.post.postId)).size > 1;
      let id = head.묶음;
      if (multiPost && !isSharedGroupId(id)) id = `g:${headRow.post.postId}:${headRow.index + 1}:${Date.now().toString(36)}`;
      for (const r of targets) {
        this.copyResult(head, r.opinion);
        r.opinion.묶음 = id;
      }
      this.picked = [];
    },
    // 머리 의견의 결과(조치결과·개선결과·기타내용)를 의견 o 에 복사
    copyResult(head, o) {
      Object.assign(o, { 조치결과: head.조치결과, 개선결과: head.개선결과, 기타내용: head.기타내용 });
    },
    // 묶음 풀기: 구성원마다 지금 묶음에 보이던 결과(머리 의견의 조치결과·개선결과)를 복사한 뒤 자기 문서 안의 새 번호를 준다.
    // 묶은 뒤 머리 의견을 고쳤으면 구성원은 아직 묶을 때 값이라, 복사하지 않으면 풀리는 순간 옛 값으로 돌아간다.
    splitGroup(row) {
      row.group.members.slice(1).forEach((m) => {
        this.copyResult(row.opinion, m.opinion);
        m.opinion.묶음 = nextGroupId(m.post.의견목록);
      });
      if (isSharedGroupId(row.opinion.묶음)) row.opinion.묶음 = nextGroupId(row.post.의견목록);
    },
    // 묶음에서 빼기: 빠지는 의견도 묶음의 현재 결과를 가지고 나간다
    leaveGroup(row) {
      this.copyResult(row.group.headOpinion, row.opinion);
      row.opinion.묶음 = nextGroupId(row.post.의견목록);
    },

    onClickEnterResults() {
      if (!this.permission.u) {
        this.customAlert("조치 결과 입력 권한이 없습니다.", "권한 없음");
        return;
      }
      this.editList = JSON.parse(JSON.stringify(this.list));
      this.picked = [];
      this.isEditMode = true;
    },
    onClickEnterResultsCancel() {
      this.isEditMode = false;
      this.editList = null;
      this.picked = [];
    },
    async onClickEnterResultsSave() {
      if (!this.permission.u) {
        this.customAlert("조치 결과 입력 권한이 없습니다.", "권한 없음");
        return;
      }

      const labels = [];
      const promises = [];
      for (const fieldId in this.editList) {
        this.syncGroups(this.editList[fieldId].posts); // 문서를 넘는 묶음도 머리 값으로 맞춘 뒤 문서마다 저장
        for (const post of this.editList[fieldId].posts) {
          const before = this.list[fieldId].posts.find((p) => p.postId === post.postId);
          if (before && JSON.stringify(before.의견목록) === JSON.stringify(post.의견목록)) continue; // 바뀐 문서만 저장

          const content = JSON.parse(post.originData.post_content);
          content.body.subInfo = applyOpinions(content.body.subInfo, post.의견목록, post.특이사항없음);
          const json = JSON.stringify(content);
          labels.push(this.editList[fieldId].label);
          promises.push(
            this.$store
              .dispatch("board/UPDATE_POST", {
                projectId: post.originData.project_id,
                boardId: post.originData.board_id,
                postId: post.originData.id,
                title: post.originData.post_title,
                content: json,
              })
              .then((r) => {
                post.originData.post_content = json;
                post.legacy = false;
                return r;
              })
          );
        }
      }

      if (!promises.length) {
        this.onClickEnterResultsCancel();
        return;
      }

      const results = await Promise.allSettled(promises);
      const fulfilledLabels = [];
      const rejectedLabels = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") fulfilledLabels.push(labels[index]);
        else rejectedLabels.push(labels[index]);
      });
      const fulfilledStr = [...new Set(fulfilledLabels)].join(", ");
      const rejectedStr = [...new Set(rejectedLabels)].join(", ");

      if (0 < rejectedStr.length) this.customAlert(`${rejectedStr}의 조치 결과 저장에 실패하였습니다.`, "저장");
      else {
        this.customAlert(`${fulfilledStr}의 조치 결과를 저장하였습니다.`, "저장");
        this.list = this.editList;
        this.isEditMode = false;
        this.editList = null;
        this.picked = [];
        this.$emit("saved");
      }
    },
    onClickRow(row) {
      if (this.isEditMode || !row.post) return;
      this.$router.push({
        name: this.routeU,
        params: {
          proj_id: this.projectId,
          boardId: this.tabBoardId,
          boardSlug: `board-${this.tabBoardId}`,
          postId: row.post.postId,
          pageType: "write",
        },
      });
    },
    updateSelectedMonth(newTab, oldTab) {
      // 올해를 보고 있고 이전 탭에서 "오늘" 기간을 보고 있었다면 새 탭에서도 오늘 기간으로 (4분기 → 11월, 11월~12월)
      if (Number(this.selectedYear) === new Date().getFullYear() && this.selectedMonth === todayPeriod(oldTab)) return todayPeriod(newTab);

      // oldTab에 따른 selectedMonth 변환
      let newSelectedMonth = this.selectedMonth;

      if (oldTab === "산업안전보건위원회") {
        // 분기 → 분기 첫 달
        newSelectedMonth = ((newSelectedMonth - 1) * 3) + 1;
      }

      // newTab에 따른 selectedMonth 변환
      if (newTab === "크레인안전협의체") {
        return newSelectedMonth % 2 ? newSelectedMonth : newSelectedMonth - 1; // 2개월 단위 시작 월
      } else if (newTab === "산업안전보건위원회") {
        return Math.ceil(newSelectedMonth / 3); // 월 → 분기
      }
      return newSelectedMonth; // 안전보건협의체·설비안전위원회는 그대로
    },
    // 게시물 불러오기 (818,819,820,821)
    async loadPost(highlightFieldId = null) {
      if (!this.selectedYear) return;

      this.isEditMode = false;
      this.editList = null;
      this.picked = [];
      this.highlightFieldId = highlightFieldId;

      const projectId = this.$route.params.proj_id;
      const page = 1;
      const perPage = 10000;
      const conditions = [];

      if (this.tabBoardId === 818) {
        // 산업안전보건위원회는 사용자가 입력하는 실시구분(header.year·quarter) 기준.
        // 회의일자는 비어 있는 문서가 있어 조회 키로 쓰지 않는다. 분기는 아래에서 getQuarter로 거른다.
        conditions.push({ key: "header.year", operator: "=", value: this.selectedYear });
      } else {
        const dateTarget = "body.monthDate";
        const date = `${this.selectedYear}-${this.selectedMonth}-01`;
        const dateSE = this.tabBoardId === 821 ? DateUtil.getTwoMonthPeriod(date) : DateUtil.getMonthDates(date);
        conditions.push({ key: dateTarget, operator: ">=", value: dateSE.start });
        conditions.push({ key: dateTarget, operator: "<=", value: dateSE.end });
      }

      const response = await this.$store.dispatch("board/LOAD_POST_LIST", { projectId, boardId: this.tabBoardId, page, perPage, conditions });
      let list = (response && response.data) || this.$store.state.board.postListInfo.data || [];
      if (this.tabBoardId === 818) list = list.filter((data) => getQuarter(JSON.parse(data.post_content).header) === this.selectedMonth);

      const _datas = {};
      for (const data of list) {
        const fieldId = data.field_id;
        const content = JSON.parse(data.post_content);
        const n = normalizeOpinions(content.body && content.body.subInfo);
        if (!_datas[fieldId]) _datas[fieldId] = { label: data.field.field_name, posts: [] };
        _datas[fieldId].posts.push({ postId: data.id, originData: data, 특이사항없음: n.특이사항없음, legacy: n.legacy, 의견목록: n.의견목록 });
      }
      this.list = _datas;
    },

    customAlert(msg, title) {
      const customAlert = new CustomEvent("customAlert", {
        detail: {
          title: title ? title : "오류가 발생했습니다",
          message: msg,
        },
      });
      window.dispatchEvent(customAlert);
    },
  },
};
</script>

<style lang="scss" scoped>
table {
  width: calc(100% - 1px);
  table-layout: fixed;
  border-collapse: collapse;
  background: #fafafa;

  tbody {
    tr {
      cursor: pointer;

      &.editMode {
        cursor: default;
      }
      &.highlight td {
        background: #fff7e6;
      }
      // 묶기 선택 중인 다른 사업장 행: 회색 배경으로 비활성처럼 보이게
      &.dim td {
        background: #f1f1f1;
        color: #aaa;
      }
    }
  }

  &,
  th,
  td {
    font-size: 14px;
    font-weight: 500;
    text-align: center;
    padding: 14px;
    vertical-align: top;

    border: 1px solid #cdcdcd;
  }

  td.text-left {
    text-align: left;
  }

  td.editMode {
    padding: 8px;
  }

  th,
  .bg-th {
    background: #fafafa;
    vertical-align: middle;
  }

  td {
    font-weight: 400;
    background: white;
  }
}

.muted {
  color: #999;
}
.tag {
  display: inline-block;
  margin: 0 6px 4px 0;
  padding: 1px 6px;
  border-radius: 4px;
  background: #eeedfd;
  color: #5e50ee;
  font-size: 12px;
}
.status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 13px;
  &.done {
    background: #e5f8ee;
    color: #1f9d55;
  }
  &.doing {
    background: #fff4e5;
    color: #e08a00;
  }
  &.etc {
    background: #fdecec;
    color: #d9534f;
  }
}
.pick,
.radio-line {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  cursor: pointer;
  input {
    margin: 0;
  }
}
.radio-line + .radio-line {
  margin-top: 4px;
}
.link {
  border: none;
  background: none;
  padding: 0;
  color: rgba(var(--vs-primary), 1);
  font-size: 13px;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}
.pick.disabled {
  color: #bbb;
  cursor: not-allowed;
}
.link:disabled {
  color: #bbb;
  cursor: not-allowed;
  text-decoration: none;
}
.join-btn {
  padding: 3px 10px;
  border: 1px solid rgba(var(--vs-primary), 1);
  border-radius: 4px;
  background: #fff;
  color: rgba(var(--vs-primary), 1);
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: rgba(var(--vs-primary), 0.08);
  }
}
</style>
